<template>
  <div>
    <div class="grid md:grid-cols-2 gap-3">
      <k-panel class="border">
        <form autocomplete="off">
          <k-input
              class="mb-4"
              id="strategyName"
              :label="$t('strategyName')"
              v-model="form.strategyName"
              :field-errors="validation.fieldErrors"/>
          <k-select
              class="mb-4"
              id="currency"
              :label="$t('currency')"
              v-model="form.params.currencyCode"
              :options="currencyOptions"
              :disabled="form.strategyUuid != null"
              value-key="value"
              label-key="label"
              :field-errors="validation.fieldErrors"/>
          <k-input
              class="mb-4"
              id="maxInvestmentInOneBorrower"
              type="number"
              :label="$t('maxInvestment')"
              v-model="form.params.maxInvestmentInOneBorrower"
              :field-errors="validation.fieldErrors"/>
          <k-input
              class=""
              id="portfolioSize"
              type="number"
              :label="$t('portfolioSize')"
              v-model="form.params.portfolioSize"
              :field-errors="validation.fieldErrors"/>
          <div class="text-xs text-gray-700 mt-1 mb-4">
            {{ $t("maximumLimit") }}
          </div>
          <div :class="validation.fieldErrors['industries'] ? 'error' : ''">
            <label class="block text-gray-700 text-sm font-semibold mb-2" for="productTypes">Industries</label>
            <a-select
                class="mb-4"
                id="industries"
                mode="multiple"
                v-model="form.params.industries"
                style="width: 100%"
                placeholder="Please select"
                :allowClear="true"
                size="large"
                :maxTagCount="3"
                @change="handleIndustryChange"
            >
              <a-select-option value="ALL">
                All industries
              </a-select-option>
              <a-select-option v-for="industry in settings.industries"
                               :key="industry.code"
                               :value="industry.code">
                {{ industry.name | enumValue }}
              </a-select-option>
            </a-select>
            <div v-if="validation.fieldErrors['industries']" class="text-red-500 text-xs italic">
              {{ validation.fieldErrors['industries'] }}
            </div>
          </div>
          <div :class="validation.fieldErrors['productTypes'] ? 'error' : ''">
            <label class="block text-gray-700 text-sm font-semibold mb-2" for="productTypes">Product
              types</label>
            <a-select
                class="mb-4"
                id="productTypes"
                mode="multiple"
                v-model="form.params.productTypes"
                style="width: 100%"
                :placeholder="$t('pleaseSelect')"
                :allowClear="true"
                size="large"
                :maxTagCount="3"
                @change="handleProductChange"
            >
              <a-select-option value="ALL">
                All product types
              </a-select-option>
              <a-select-option v-for="productType in settings.productTypes"
                               :key="productType.code"
                               :value="productType.code">
                {{ productType.name }}
              </a-select-option>
            </a-select>
            <div v-if="validation.fieldErrors['productTypes']" class="text-red-500 text-xs italic">
              {{ validation.fieldErrors['productTypes'] }}
            </div>
          </div>
          <div :class="validation.fieldErrors['countries'] ? 'error' : ''">
            <label class="block text-gray-700 text-sm font-semibold mb-2" for="countries">Countries</label>
            <a-select
                class="mb-4"
                id="countries"
                mode="multiple"
                v-model="form.params.countries"
                style="width: 100%"
                :placeholder="$t('pleaseSelect')"
                :allowClear="true"
                size="large"
                :maxTagCount="3"
                @change="handleCountryChange"
            >
              <a-select-option value="ALL">
                All countries
              </a-select-option>
              <a-select-option v-for="country in settings.countries"
                               :key="country.code"
                               :value="country.code">
                {{ country.name }}
              </a-select-option>
            </a-select>
            <div v-if="validation.fieldErrors['countries']" class="text-red-500 text-xs italic">
              {{ validation.fieldErrors['countries'] }}
            </div>
          </div>
          <div :class="validation.fieldErrors['loanOriginators'] ? 'error' : ''">
            <label class="block text-gray-700 text-sm font-semibold mb-2" for="loanOriginators">
              {{ $t("loanOriginator") }}</label>
            <a-select
                class="mb-4"
                id="loanOriginators"
                mode="multiple"
                v-model="form.params.loanOriginators"
                style="width: 100%"
                :placeholder="$t('pleaseSelect')"
                :allowClear="true"
                size="large"
                :maxTagCount="3"
                @change="handleOriginatorChange"
            >
              <a-select-option value="ALL">
                All originators
              </a-select-option>
              <a-select-option v-for="originator in settings.loanOriginators"
                               :key="originator.code"
                               :value="originator.code">
                {{ originator.name }}
              </a-select-option>
            </a-select>
            <div v-if="validation.fieldErrors['loanOriginators']" class="text-red-500 text-xs italic">
              {{ validation.fieldErrors['loanOriginators'] }}
            </div>
          </div>
        </form>
      </k-panel>
      <form autocomplete="off">
        <k-panel style="height: 100%;" class="border">
          <div>
            <k-slider
                class="mb-4"
                id="interestRate"
                :label="$t('interestRate')"
                v-model="interestRate"
                :min="settings.minInterestRate"
                :max="settings.maxInterestRate"
                tooltipFormat="{value}%"
                :field-errors="validation.fieldErrors"/>
            <k-slider
                class="mb-4"
                id="remainingLoanTerm"
                :label="$t('remainingTerm')"
                :min="settings.minRemainingLoanTerm"
                :max="settings.maxRemainingLoanTerm"
                v-model="remainingLoanTerm"
                :field-errors="validation.fieldErrors"/>
          </div>
          <div slot="footer">
            <a-checkbox
                id="acceptTerms"
                class="mt-3"
                v-model="form.acceptTerms">
              {{ $t("readTerms") }}
            </a-checkbox>
            <a @click="onPreview"
               target="_blank"
               class="text-primary-500">
              {{ $t("noteOfAcceptance") }}
            </a>
            <div v-if="validation.fieldErrors['acceptTerms']" class="text-red-500 text-xs italic">
              {{ validation.fieldErrors['acceptTerms'] }}
            </div>
          </div>
        </k-panel>
      </form>
    </div>
    <div class="text-center mt-6">
      <p v-if="!availableInvestments.borrowerCount">{{ $t("noInvestments") }}</p>
      <p v-if="availableInvestments.borrowerCount"> {{
          $t("investPreview",
                        [
              this.$options.filters.numeralFormat(availableInvestments.amount, '$0,0.00'),
         availableInvestments.borrowerCount
                        ]
                    )
                }}</p>
    </div>
    <div class="text-center mt-6">
      <k-button @click.native.prevent="onSave">
        {{ $t("saveAndInvest") }}
      </k-button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  components: {},
  model: {
    prop: 'form',
    event: 'change'
  },
  props: {
    form: {
      type: Object,
      required: true
    },
    availableInvestments: {
      type: Object,
      required: true
    },
    accounts: {
      type: Array,
      required: true
    },
    validation: {
      type: Object,
      required: true
    },
    settings: {
      type: Object,
      required: true
    }
  },
  watch: {
    params: {
      handler: _.debounce(function () {
        this.onFormChange();
      }, 500),
      deep: true
    }
  },
  computed: {
    params() {
      return this.form.params;
    },
    interestRate: {
      get() {
        return [
          this.form.params.minInterestRate, this.form.params.maxInterestRate
        ];
      },
      set(value) {
        this.form.params.minInterestRate = value[0];
        this.form.params.maxInterestRate = value[1];
      }
    },
    remainingLoanTerm: {
      get() {
        return [
          this.form.params.minRemainingLoanTerm, this.form.params.maxRemainingLoanTerm
        ];
      },
      set(value) {
        this.form.params.minRemainingLoanTerm = value[0];
        this.form.params.maxRemainingLoanTerm = value[1];
      }
    },
    currencyOptions() {
      return this.accounts.map(a => {
        return {
          value: a.currencyCode,
          label: a.currencyCode
        }
      })
    }
  },
  methods: {
    async onFormChange() {
      this.$emit('change', this.form);
    },
    async onSave() {
      this.$emit('save', this.form);
    },
    async onPreview() {
      this.$emit('preview', this.form);
    },
    async handleIndustryChange(value) {
      if (value.includes('ALL')) {
        this.form.industries = ['ALL'];
      } else {
        this.form.industries = value;
      }
    },
    async handleProductChange(value) {
      if (value.includes('ALL')) {
        this.form.productTypes = ['ALL'];
      } else {
        this.form.productTypes = value;
      }
    },
    async handleCountryChange(value) {
      if (value.includes('ALL')) {
        this.form.countries = ['ALL'];
      } else {
        this.form.countries = value;
      }
    },
    async handleOriginatorChange(value) {
      if (value.includes('ALL')) {
        this.form.loanOriginators = ['ALL'];
      } else {
        this.form.loanOriginators = value;
      }
    },
  }
};
</script>


